import { Fragment, useEffect, useRef } from "react";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useNow } from "../../hooks/useNow";
import { useMakeUrl, routes } from "../../lib/urls";
import {
  SearchableDropdown,
  Divider,
  SectionLabel,
  RowButton,
  RowLink,
} from "../SearchableDropdown";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { chatLabel, pickChatIconColor } from "./pickChatIconColor";

const RECENTLY_CLOSED_WINDOW_MS = 96 * 60 * 60 * 1000;

type Props = {
  opener: React.ReactNode;
  tabs: readonly {
    id: string;
    name: string;
    lastActivityAt: number | null;
  }[];
  activeTabId: string | null;
  userId?: string;
  closedChats: readonly {
    id: string;
    name: string;
    closedAt: number;
    lastActivityAt: number;
  }[];
  onReopenChat?: ((id: string) => void) | undefined;
  onNewTab?: (() => void) | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
};

function ChatRowContent({
  id,
  name,
  secondary,
  active,
}: {
  id: string;
  name: string;
  secondary?: string;
  active?: boolean;
}) {
  return (
    <>
      <Icon
        name="ask"
        size={16}
        color={`var(--${pickChatIconColor(id)}-600)`}
        style={{ flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {chatLabel(name)}
        </Text>
        {secondary && (
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {secondary}
          </Text>
        )}
      </div>
      {active && (
        <Icon
          name="check"
          size={12}
          color="var(--text-secondary)"
          style={{ flexShrink: 0 }}
        />
      )}
    </>
  );
}

export function ChatTabsDropdown({
  opener,
  tabs,
  activeTabId,
  userId,
  closedChats,
  onReopenChat,
  onNewTab,
  onOpenChange,
}: Props) {
  const makeUrl = useMakeUrl();
  const formatRelativeTime = useFormatRelativeTime();
  const openRef = useRef<(() => void) | null>(null);
  const cutoff = useNow(60_000) - RECENTLY_CLOSED_WINDOW_MS;

  useEffect(() => {
    // Match on e.code, not e.key: on macOS Option composes a glyph (⇧⌥C → Ç).
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.shiftKey &&
        e.altKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        e.code === "KeyC"
      ) {
        e.preventDefault();
        openRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <SearchableDropdown
      aria-label="Chats"
      opener={opener}
      align="left"
      searchPlaceholder="Search chats…"
      openRef={openRef}
      onOpenChange={onOpenChange}
    >
      {(query, close) => {
        const lowerQuery = query.toLowerCase().trim();
        const matches = (name: string) =>
          chatLabel(name).toLowerCase().includes(lowerQuery);

        const sections = [
          {
            label: "Open chats",
            rows: tabs
              .filter((tab) => matches(tab.name))
              .map((tab) => (
                <RowLink
                  key={tab.id}
                  to={makeUrl(routes.chats, { tabId: tab.id, userId })}
                  onClick={() => close()}
                >
                  <ChatRowContent
                    id={tab.id}
                    name={tab.name}
                    secondary={
                      tab.lastActivityAt == null
                        ? undefined
                        : formatRelativeTime(tab.lastActivityAt)
                    }
                    active={tab.id === activeTabId}
                  />
                </RowLink>
              )),
          },
          ...[
            {
              label: "Recently closed",
              chats: closedChats.filter((chat) => chat.closedAt > cutoff),
            },
            {
              label: "Full history",
              chats: closedChats
                .filter((chat) => chat.closedAt <= cutoff)
                .toSorted((a, b) => b.lastActivityAt - a.lastActivityAt),
            },
          ].map(({ label, chats }) => ({
            label,
            rows: onReopenChat
              ? chats
                  .filter((chat) => matches(chat.name))
                  .map((chat) => (
                    <RowButton
                      key={chat.id}
                      onClick={() => {
                        onReopenChat(chat.id);
                        close();
                      }}
                    >
                      <ChatRowContent
                        id={chat.id}
                        name={chat.name}
                        secondary={formatRelativeTime(chat.lastActivityAt)}
                      />
                    </RowButton>
                  ))
              : [],
          })),
        ].filter((section) => section.rows.length > 0);

        if (sections.length === 0) {
          if (!onNewTab) return null;
          return (
            <RowButton
              onClick={() => {
                onNewTab();
                close();
              }}
            >
              <Icon name="plus" size={16} style={{ flexShrink: 0 }} />
              <Text variant="text-regular" color="var(--text-primary)" ellipsis>
                Open a new tab
              </Text>
            </RowButton>
          );
        }

        return sections.map((section, i) => (
          <Fragment key={section.label}>
            {i > 0 && <Divider />}
            <SectionLabel>
              <Text variant="text-small" color="var(--text-secondary)">
                {section.label}
              </Text>
            </SectionLabel>
            {section.rows}
          </Fragment>
        ));
      }}
    </SearchableDropdown>
  );
}
